import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown, Minus, AlertTriangle, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import api from '@/lib/api';
import { getRiskColor, getRiskLevel, getRiskBadgeClass } from '@/lib/utils';
import { useUserProfile } from '@/lib/useUserProfile';
import { NavBar } from '@/components/NavBar';

const SectorsPage = () => {
  const navigate = useNavigate();
  const { sectorDisplayName } = useUserProfile();
  const [loading, setLoading] = useState(true);
  const [sectors, setSectors] = useState([]);

  useEffect(() => {
    fetchSectors();
  }, []);

  const fetchSectors = async () => {
    try {
      const response = await api.getSectors();
      setSectors(response.data);
    } catch (error) {
      console.error('Error fetching sectors:', error);
    } finally {
      setLoading(false);
    }
  };

  const TrendIcon = ({ trend }) => {
    switch (trend?.toLowerCase()) {
      case 'up': return <TrendingUp className="h-4 w-4 text-[#FF3B30]" />;
      case 'down': return <TrendingDown className="h-4 w-4 text-[#2E7D32]" />;
      default: return <Minus className="h-4 w-4 text-[#4E6974]" />;
    }
  };

  const sectorIcons = {
    'Agribusiness': '🌾',
    'Mining and resources': '⛏️',
    'Defence': '🛡️',
    'Government procurement': '🏛️',
    'Mid-market importers': '📦'
  };

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      {/* Navigation */}
      <NavBar />

      <main className="pt-24 px-4 lg:px-6 pb-8">
        <div className="max-w-[1400px] mx-auto">
          {/* Header */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-8 mb-8"
          >
            <h1 className="text-3xl font-bold text-[#0D3B4F]" data-testid="sectors-title">
              Sector risk overview
            </h1>
            <p className="text-[#4E6974] mt-2">
              Risk assessment across key industry sectors for global businesses
            </p>
          </motion.div>

          {/* Sectors Grid */}
          {loading ? (
            <div className="text-center py-12 text-[#4E6974]">Loading sectors...</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...sectors].sort((a, b) => {
                if (a.sector_name === sectorDisplayName) return -1;
                if (b.sector_name === sectorDisplayName) return 1;
                return 0;
              }).map((sector, index) => {
                const isUserSector = sector.sector_name === sectorDisplayName;
                return (
                <motion.div
                  key={sector.id || sector.sector_name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card 
                    className={`bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none hover:shadow-[0_8px_32px_rgba(0,0,0,0.08)] transition-all hover:-translate-y-1 cursor-pointer group ${isUserSector ? 'ring-2 ring-[#0097A7]' : ''}`}
                    onClick={() => navigate(`/sectors/${encodeURIComponent(sector.sector_name)}`)}
                    data-testid={`sector-card-${index}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <span className="text-4xl">{sectorIcons[sector.sector_name] || sector.sector_icon}</span>
                        <div className="flex items-center gap-2">
                          {isUserSector && <Badge className="bg-[#00707C] text-white text-[9px]">Your sector</Badge>}
                          <Badge className={`${getRiskBadgeClass(getRiskLevel(sector.overall_risk_score))} text-[10px]`}>
                            {getRiskLevel(sector.overall_risk_score)}
                          </Badge>
                        </div>
                      </div>
                      
                      <h3 className="text-lg font-bold text-[#0D3B4F] mb-2 group-hover:text-[#0097A7] transition-colors">
                        {sector.sector_name}
                      </h3>
                      
                      <p className="text-sm text-[#4E6974] mb-4 line-clamp-2">
                        {sector.description}
                      </p>
                      
                      <div className="flex items-center justify-between pt-4 border-t border-[#E0E0E0]">
                        <div className="flex items-center gap-2">
                          <span 
                            className="text-2xl font-bold"
                            style={{ color: getRiskColor(sector.overall_risk_score) }}
                          >
                            {sector.overall_risk_score}
                          </span>
                          <span className="text-[#4E6974] text-sm">/100</span>
                          <TrendIcon trend={sector.risk_trend} />
                        </div>
                        
                        <div className="flex items-center gap-2 text-sm text-[#4E6974]">
                          <AlertTriangle className="h-4 w-4 text-[#FF9500]" />
                          <span>{sector.countries_elevated} elevated</span>
                        </div>
                      </div>
                      
                      <div className="mt-4 pt-4 border-t border-[#E0E0E0]">
                        <p className="text-xs text-[#4E6974] mb-1">Top signal:</p>
                        <p className="text-sm text-[#0D3B4F] line-clamp-1">{sector.top_signal}</p>
                      </div>
                      
                      <div className="mt-4 flex items-center text-[#0097A7] text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                        View sector details <ChevronRight className="h-4 w-4 ml-1" />
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default SectorsPage;
